import {
  formatShowDateLine,
  parseShowDateTime,
  resolveDoorsOpenMoment,
} from "@/app/_utils/showDateTime";

export type ParsedShowInfo = {
  dateLine: string;
  introParagraphs: string[];
  perksCallout: string | null;
  doorsTime: string | null;
  showTime: string | null;
  locationLines: string[];
  timingNote: string | null;
  recordingPolicy: string | null;
  ageRestriction: string | null;
  inquiriesText: string | null;
  instagramHandle: string | null;
  email: string | null;
};

const DEFAULT_TIMING_NOTE =
  "An important note on timing: doors open time for all shows is 30 minutes prior to showtime. Come early to enjoy some pre-show drinks.";
const DEFAULT_RECORDING_POLICY =
  "While we encourage you to take pictures and videos of the space, filming the comics is strictly prohibited.";
const DEFAULT_AGE_RESTRICTION =
  "This is a 21+ event - security will be checking tickets and ID's.";
const DEFAULT_INQUIRIES =
  "If you have any questions, please reach out to us via Instagram @comedy.uo or email us at admin@comedyuo.com";

/** Shows that should not show the default 21+ callout on the ticket page. */
const NO_DEFAULT_AGE_RESTRICTION_SLUGS = new Set(["nyu-hillel-comedy-show"]);

function defaultInstagramHandle(description: string): string {
  if (/@magic\.uo/i.test(description)) return "magic.uo";
  return "comedy.uo";
}

const DIVIDER_LINE_RE = /^-{3,}\s*$/;

function isFooterBoilerplateParagraph(text: string): boolean {
  const t = text.trim();
  return (
    TIMING_NOTE_RE.test(t) ||
    RECORDING_RE.test(t) ||
    AGE_RE.test(t) ||
    INQUIRIES_RE.test(t)
  );
}

function splitMainAndFooter(trimmed: string): {
  mainRaw: string;
  footerRaw: string;
  hasFooterDivider: boolean;
} {
  const lines = trimmed.replace(/\r\n?/g, "\n").split("\n");
  const dividerIndex = lines.findIndex((line) => DIVIDER_LINE_RE.test(line.trim()));
  if (dividerIndex === -1) {
    return { mainRaw: trimmed, footerRaw: "", hasFooterDivider: false };
  }
  return {
    mainRaw: lines.slice(0, dividerIndex).join("\n").trim(),
    footerRaw: lines.slice(dividerIndex + 1).join("\n").trim(),
    hasFooterDivider: true,
  };
}
const WEEKDAY_DATE_RE =
  /^(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\b/i;
const DOORS_LINE_RE = /^doors?\s*open[s]?\s*:\s*(.+)$/i;
const SHOW_START_LINE_RE = /^show\s*start[s]?\s*:\s*(.+)$/i;
const PERKS_RE = /^(tickets include|beer and wine included)/i;
const TIMING_HEADER_RE = /^show timing\s*:?\s*$/i;
const LOCATION_HEADER_RE = /^location\s*:?\s*$/i;
const LOCATION_INLINE_RE = /^location\s*:\s*(.+)$/i;
const ADDRESS_LINE_RE = /^address\b/i;
const STREET_ADDRESS_RE =
  /^\d+\s+.+\b(st|street|ave|avenue|rd|road|blvd|boulevard|lane|ln|dr|drive|way|pl|place|ct|court)\b\.?$/i;
const GENERIC_LOCATION_NAMES = new Set([
  "blank",
  "n/a",
  "na",
]);
const TIMING_NOTE_RE = /^an important note on timing/i;
const RECORDING_RE = /^while we encourage/i;
const AGE_RE = /^this is a 21\+/i;
const INQUIRIES_RE = /^if you have any questions/i;
const INSTAGRAM_RE = /@([a-z0-9._]+)/i;
const EMAIL_RE = /([a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,})/i;

function ordinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

function formatDateLineWithOrdinal(start?: string | null): string {
  const d = parseShowDateTime(start ?? undefined);
  if (!d?.isValid()) return "";
  try {
    const day = d.date();
    const monthPart = safeFormat(d, "dddd, MMMM");
    if (!monthPart) return "";
    return `${monthPart} ${day}${ordinalSuffix(day)}`;
  } catch {
    return "";
  }
}

function normalizeTimeLabel(raw: string): string {
  const trimmed = raw.trim().replace(/\s+/g, " ");
  if (!trimmed) return "";
  const upper = trimmed.toUpperCase();
  if (/AM|PM/.test(upper)) {
    return upper
      .replace(/\s*(AM|PM)\s*/i, " $1")
      .replace(/\b(am|pm)\b/g, (m) => m.toUpperCase());
  }
  const minutes = timeLabelToMinutes(trimmed);
  if (minutes != null) {
    const h24 = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const meridiem = h24 >= 12 ? "PM" : "AM";
    const h12 = h24 % 12 || 12;
    return mins ? `${h12}:${String(mins).padStart(2, "0")} ${meridiem}` : `${h12} ${meridiem}`;
  }
  return trimmed;
}

function formatWallClock(start?: string | null, doorsOpen?: string | null): {
  doors: string | null;
  show: string | null;
} {
  const showMoment = parseShowDateTime(start ?? undefined);
  const doorsMoment = resolveDoorsOpenMoment(start, doorsOpen);
  return {
    doors: safeFormatWallClock(doorsMoment),
    show: safeFormatWallClock(showMoment),
  };
}

function safeFormatWallClock(moment: ReturnType<typeof parseShowDateTime>): string | null {
  if (!moment?.isValid()) return null;
  try {
    return moment.format("h:mm A");
  } catch {
    return null;
  }
}

/**
 * Minutes since midnight for a wall-clock label like "8 PM", "8:30 PM", "20:30".
 * Parsed directly (no dayjs) — WebKit throws on dayjs.tz("2000-01-01 7:30 PM").
 */
function timeLabelToMinutes(label: string | null): number | null {
  if (!label) return null;
  const m = label.trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?$/i);
  if (!m) return null;
  let hours = parseInt(m[1], 10);
  const minutes = m[2] ? parseInt(m[2], 10) : 0;
  if (hours > 23 || minutes > 59) return null;
  const meridiem = m[3]?.toUpperCase();
  if (meridiem === "PM" && hours !== 12) hours += 12;
  else if (meridiem === "AM" && hours === 12) hours = 0;
  return hours * 60 + minutes;
}

/** Whole-minute gap between the shown doors-open and show-start labels. */
function doorsLeadMinutes(
  doorsTime: string | null,
  showTime: string | null
): number | null {
  const doors = timeLabelToMinutes(doorsTime);
  const show = timeLabelToMinutes(showTime);
  if (doors == null || show == null) return null;
  const diff = show - doors;
  return diff > 0 ? diff : null;
}

/** Rewrite the "N minutes prior to showtime" figure to the real doors lead. */
function applyDoorsLeadToTimingNote(
  note: string,
  minutes: number | null
): string {
  if (minutes == null) return note;
  return note.replace(
    /\b\d+\s*minutes?(\s+prior\s+to\s+showtime)/i,
    `${minutes} minute${minutes === 1 ? "" : "s"}$1`
  );
}

function paragraphsFromText(text: string): string[][] {
  const lines = text
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((l) => l.trimEnd());
  const paragraphs: string[][] = [];
  let current: string[] = [];

  for (const line of lines) {
    if (!line.trim()) {
      if (current.length) {
        paragraphs.push(current);
        current = [];
      }
      continue;
    }
    current.push(line.trim());
  }
  if (current.length) paragraphs.push(current);
  return paragraphs;
}

function paragraphText(lines: string[]): string {
  return lines.join("\n").trim();
}

function isTimingParagraph(lines: string[]): boolean {
  if (lines.length === 0) return false;
  if (TIMING_HEADER_RE.test(lines[0])) return true;
  return lines.some(
    (l) => DOORS_LINE_RE.test(l) || SHOW_START_LINE_RE.test(l)
  );
}

function parseTimingParagraph(lines: string[]): {
  doors: string | null;
  show: string | null;
} {
  let doors: string | null = null;
  let show: string | null = null;
  for (const line of lines) {
    const doorsMatch = line.match(DOORS_LINE_RE);
    if (doorsMatch) doors = normalizeTimeLabel(doorsMatch[1]);
    const showMatch = line.match(SHOW_START_LINE_RE);
    if (showMatch) show = normalizeTimeLabel(showMatch[1]);
  }
  return { doors, show };
}

function isGenericLocationValue(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  if (!normalized) return true;
  return GENERIC_LOCATION_NAMES.has(normalized);
}

function looksLikeStreetAddress(line: string): boolean {
  const trimmed = line.trim();
  return STREET_ADDRESS_RE.test(trimmed) || ADDRESS_LINE_RE.test(trimmed);
}

function normalizeLocationLine(line: string): string {
  const trimmed = line.trim();
  const inlineLocation = trimmed.match(LOCATION_INLINE_RE);
  if (inlineLocation) return inlineLocation[1].trim();

  const addressMatch = trimmed.match(/^address\s*:?\s*(.*)$/i);
  if (addressMatch) {
    const rest = addressMatch[1].trim();
    if (!rest) return trimmed;
    if (/^tba$/i.test(rest)) return "Address TBA";
    return rest;
  }

  return trimmed;
}

function isLocationParagraph(lines: string[]): boolean {
  if (!lines.length) return false;
  if (LOCATION_HEADER_RE.test(lines[0])) return true;
  if (LOCATION_INLINE_RE.test(lines[0])) return true;
  if (lines.some((line) => looksLikeStreetAddress(line))) return true;
  if (lines.length >= 2 && lines.some((line) => ADDRESS_LINE_RE.test(line.trim()))) return true;
  return false;
}

function parseLocationParagraph(lines: string[]): string[] {
  if (LOCATION_HEADER_RE.test(lines[0])) {
    return lines
      .slice(1)
      .map(normalizeLocationLine)
      .filter((line) => line.length > 0);
  }

  return lines.map(normalizeLocationLine).filter((line) => line.length > 0);
}

function dedupeLocationLines(lines: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const line of lines) {
    const key = line.trim().toLowerCase();
    if (!key || isGenericLocationValue(line)) continue;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(line.trim());
  }
  return result;
}

function resolveLocationLines(
  parsedLines: string[],
  locationName?: string | null
): string[] {
  if (parsedLines.length > 0) {
    return dedupeLocationLines(parsedLines);
  }

  if (locationName && !isGenericLocationValue(locationName) && locationName !== "Location TBA") {
    return [locationName.trim()];
  }

  return [];
}

function normalizePerksCallout(text: string, slug?: string | null): string {
  if (slug === "4th-annual-hamptons-comedy-show") {
    return text.replace(/\bunlimited drinks\b/gi, "free drinks");
  }
  return text;
}

export function parseShowInfo(
  description: string,
  options?: {
    start?: string | null;
    doorsOpen?: string | null;
    locationName?: string | null;
    slug?: string | null;
    /** Multi-performance show: the row's own times beat the shared description. */
    preferProvidedTimes?: boolean;
  }
): ParsedShowInfo {
  const trimmed = description.trim();
  const { mainRaw, footerRaw, hasFooterDivider } = splitMainAndFooter(trimmed);
  const mainParagraphs = paragraphsFromText(mainRaw);
  const footerParagraphs = paragraphsFromText(footerRaw).map(paragraphText);

  const introParagraphs: string[] = [];
  const locationLines: string[] = [];
  let perksCallout: string | null = null;
  let parsedDoors: string | null = null;
  let parsedShow: string | null = null;
  let dateLine = "";

  for (let i = 0; i < mainParagraphs.length; i += 1) {
    const lines = mainParagraphs[i];
    const text = paragraphText(lines);

    if (!dateLine && WEEKDAY_DATE_RE.test(lines[0] ?? "")) {
      dateLine = lines[0];
      const rest = lines.slice(1);
      if (rest.length) introParagraphs.push(paragraphText(rest));
      continue;
    }

    if (PERKS_RE.test(text)) {
      perksCallout = normalizePerksCallout(text, options?.slug);
      continue;
    }

    if (isTimingParagraph(lines)) {
      const timing = parseTimingParagraph(lines);
      parsedDoors = timing.doors ?? parsedDoors;
      parsedShow = timing.show ?? parsedShow;
      continue;
    }

    if (isLocationParagraph(lines)) {
      locationLines.push(...parseLocationParagraph(lines));
      continue;
    }

    if (isFooterBoilerplateParagraph(text)) {
      continue;
    }

    introParagraphs.push(text);
  }

  // Pull location/address blocks out of intro if they were not caught above.
  const refinedIntro: string[] = [];
  for (const paragraph of introParagraphs) {
    const lines = paragraph
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    if (isLocationParagraph(lines)) {
      locationLines.push(...parseLocationParagraph(lines));
      continue;
    }
    refinedIntro.push(paragraph);
  }
  introParagraphs.length = 0;
  introParagraphs.push(...refinedIntro);

  if (!dateLine) {
    dateLine =
      formatDateLineWithOrdinal(options?.start) ||
      formatShowDateLine(options?.start) ||
      "";
  }

  const apiTimes = formatWallClock(options?.start, options?.doorsOpen);
  // One description cannot describe two performances, so a double header reads its
  // times off the selected row and falls back to the parsed ones.
  const doorsTime = options?.preferProvidedTimes
    ? apiTimes.doors ?? parsedDoors
    : parsedDoors ?? apiTimes.doors;
  const showTime = options?.preferProvidedTimes
    ? apiTimes.show ?? parsedShow
    : parsedShow ?? apiTimes.show;

  let timingNote: string | null = null;
  let recordingPolicy: string | null = null;
  let ageRestriction: string | null = null;
  let inquiriesText: string | null = null;
  let instagramHandle: string | null = null;
  let email: string | null = null;

  for (const p of footerParagraphs) {
    if (TIMING_NOTE_RE.test(p)) timingNote = p;
    else if (RECORDING_RE.test(p)) recordingPolicy = p;
    else if (AGE_RE.test(p)) ageRestriction = p;
    else if (INQUIRIES_RE.test(p)) {
      inquiriesText = p;
      const ig = p.match(INSTAGRAM_RE);
      const em = p.match(EMAIL_RE);
      instagramHandle = ig?.[1] ?? null;
      email = em?.[1] ?? null;
    }
  }

  if (!instagramHandle && inquiriesText) {
    const ig = inquiriesText.match(INSTAGRAM_RE);
    instagramHandle = ig?.[1] ?? null;
  }
  if (!email && inquiriesText) {
    const em = inquiriesText.match(EMAIL_RE);
    email = em?.[1] ?? null;
  }

  if (!hasFooterDivider) {
    if (!timingNote) timingNote = DEFAULT_TIMING_NOTE;
    if (!recordingPolicy) recordingPolicy = DEFAULT_RECORDING_POLICY;
    if (
      !ageRestriction &&
      !NO_DEFAULT_AGE_RESTRICTION_SLUGS.has(String(options?.slug ?? "").trim())
    ) {
      ageRestriction = DEFAULT_AGE_RESTRICTION;
    }
    if (!inquiriesText) inquiriesText = DEFAULT_INQUIRIES;
    if (!instagramHandle) instagramHandle = defaultInstagramHandle(trimmed);
    if (!email) email = "admin@comedyuo.com";
  } else {
    if (!instagramHandle) instagramHandle = defaultInstagramHandle(trimmed);
    if (!email) email = "admin@comedyuo.com";
  }

  if (NO_DEFAULT_AGE_RESTRICTION_SLUGS.has(String(options?.slug ?? "").trim())) {
    ageRestriction = null;
  }

  if (timingNote) {
    timingNote = applyDoorsLeadToTimingNote(
      timingNote,
      doorsLeadMinutes(doorsTime, showTime)
    );
  }

  const resolvedLocationLines = resolveLocationLines(locationLines, options?.locationName);

  return {
    dateLine,
    introParagraphs: introParagraphs.filter(Boolean),
    perksCallout,
    doorsTime,
    showTime,
    locationLines: resolvedLocationLines,
    timingNote,
    recordingPolicy,
    ageRestriction,
    inquiriesText,
    instagramHandle,
    email,
  };
}

export type PerksTextPart = { text: string; bold: boolean };

/** Split perks copy into plain / bold segments for callout styling. */
export function splitPerksForEmphasis(text: string): PerksTextPart[] {
  const re =
    /\b(unlimited drinks|beer and wine|light bites(?:, desserts)?(?: and free drinks)?|free drinks|frozen yogurt|BuddhaBerry|desserts)\b/gi;
  const parts: PerksTextPart[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      parts.push({ text: text.slice(last, match.index), bold: false });
    }
    parts.push({ text: match[0], bold: true });
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last), bold: false });
  if (!parts.length) parts.push({ text, bold: false });
  return parts;
}
